import React, { useState, useEffect, useRef } from "react";
import { useNavigate } from "react-router-dom";
import { formatDate } from "../utils/formatDate";
import {
  Upload,
  RefreshCw,
  Zap,
  CheckCircle,
  XCircle,
  AlertCircle,
  ArrowLeftRight,
  FileText,
  Eye,
  Trash2,
  ChevronLeft,
  ChevronRight,
} from "lucide-react";
import { v1Compare, apiCall } from "../api";
import "./V1Comparison.css";

const V1Comparison = () => {
  const navigate = useNavigate();
  const fileInputRef = useRef(null);

  const [comparisons, setComparisons] = useState([]);
  const [pagination, setPagination] = useState({
    total: 0, page: 1, pages: 1, limit: 10, skip: 0,
  });
  const [loading, setLoading] = useState(true);
  const [uploading, setUploading] = useState(false);
  const [error, setError] = useState("");
  const [searchTerm, setSearchTerm] = useState("");

  useEffect(() => {
    fetchComparisons();
  }, []);

  const fetchComparisons = async (skip = 0) => {
    try {
      setLoading(true);
      setError("");
      const response = await apiCall(`/v1/comparisons?skip=${skip}&limit=10`);
      
      if (response.data) {
        setComparisons(response.data);
        setPagination(response.pagination || { total: response.data.length, page: 1, pages: 1, limit: 10, skip: 0 });
      } else if (Array.isArray(response)) {
        setComparisons(response);
      }
    } catch (err) {
      // If endpoint doesn't exist yet, show empty state
      console.log("Comparisons endpoint:", err.message);
      setComparisons([]);
    } finally {
      setLoading(false);
    }
  };

  const handleFileUpload = async (event) => {
    const file = event.target.files?.[0];
    if (!file) return;

    if (!file.name.toLowerCase().endsWith(".pdf")) {
      setError("Only PDF files are supported");
      return;
    }

    try {
      setUploading(true);
      setError("");

      const result = await v1Compare(file);
      
      // Navigate to comparison detail page if we have an ID
      if (result.comparison_id) {
        navigate(`/v1/comparison/${result.comparison_id}`);
      } else {
        // Refresh list if no ID returned
        fetchComparisons();
      }
    } catch (err) {
      setError(`Comparison failed: ${err.message}`);
    } finally {
      setUploading(false);
      if (fileInputRef.current) fileInputRef.current.value = "";
    }
  };

  const handleDelete = async (comparisonId, e) => {
    e.stopPropagation();
    if (!window.confirm("Delete this comparison?")) return;

    try {
      await apiCall(`/v1/comparisons/${comparisonId}`, { method: "DELETE" });
      fetchComparisons(pagination.skip);
    } catch (err) {
      setError(`Delete failed: ${err.message}`);
    }
  };

  const handleNextPage = () => {
    if (pagination.page < pagination.pages) {
      fetchComparisons(pagination.skip + pagination.limit);
    }
  };

  const handlePrevPage = () => {
    if (pagination.page > 1) {
      fetchComparisons(Math.max(0, pagination.skip - pagination.limit));
    }
  };

  const filteredComparisons = comparisons.filter((comp) => {
    const term = searchTerm.toLowerCase();
    return (
      comp._id?.toLowerCase().includes(term) ||
      comp.filename?.toLowerCase().includes(term)
    );
  });

  const getMatchPercentage = (comp) => {
    if (!comp.match_stats) return 0;
    const { total, matches } = comp.match_stats;
    return total > 0 ? Math.round((matches / total) * 100) : 0;
  };

  return (
    <div className="v1-comparison">
      {/* Header */}
      <div className="v1-comp-header">
        <div className="v1-comp-header-info">
          <h2>
            <ArrowLeftRight size={24} />
            Model Comparison
          </h2>
          <p>Compare extraction results between XOrbit (Gemini) and VJA (Qwen) models</p>
        </div>
        <div className="v1-comp-header-actions">
          <input
            type="file"
            ref={fileInputRef}
            onChange={handleFileUpload}
            accept=".pdf"
            style={{ display: "none" }}
          />
          <button
            className="v1-btn-compare"
            onClick={() => fileInputRef.current?.click()}
            disabled={uploading}
          >
            {uploading ? (
              <>
                <RefreshCw size={18} className="spin" /> Comparing...
              </>
            ) : (
              <>
                <Upload size={18} /> Upload PDF to Compare
              </>
            )}
          </button>
        </div>
      </div>

      {error && (
        <div className="v1-error-banner">
          {error}
          <button onClick={() => setError("")}>×</button>
        </div>
      )}

      {/* Loading Animation for Upload */}
      {uploading && (
        <div className="v1-comp-loading">
          <div className="v1-comp-loading-animation">
            <div className="v1-comp-loading-model xorbit">
              <Zap size={32} />
              <span>XOrbit</span>
              <RefreshCw size={20} className="spin" />
            </div>
            <ArrowLeftRight size={24} className="v1-comp-loading-arrow" />
            <div className="v1-comp-loading-model vja">
              <Zap size={32} />
              <span>VJA</span>
              <RefreshCw size={20} className="spin" />
            </div>
          </div>
          <p>Extracting with both models and comparing results...</p>
        </div>
      )}

      {/* Stats Summary */}
      <div className="v1-comp-stats-row">
        <div className="v1-comp-stat-item">
          <FileText size={20} />
          <span className="v1-comp-stat-value">{pagination.total || comparisons.length}</span>
          <span className="v1-comp-stat-label">Total Comparisons</span>
        </div>
      </div>

      {/* Filters */}
      <div className="v1-comp-filters">
        <input
          type="text"
          placeholder="Search by ID or filename..."
          className="v1-comp-search"
          value={searchTerm}
          onChange={(e) => setSearchTerm(e.target.value)}
        />
        <button className="v1-comp-refresh-btn" onClick={() => fetchComparisons(0)}>
          <RefreshCw size={16} /> Refresh
        </button>
      </div>

      {/* Comparisons List */}
      <div className="v1-comp-list-container">
        {loading ? (
          <div className="v1-comp-list-loading">
            <RefreshCw size={24} className="spin" />
            <p>Loading comparisons...</p>
          </div>
        ) : filteredComparisons.length === 0 ? (
          <div className="v1-comp-empty">
            <ArrowLeftRight size={64} />
            <h3>No Comparisons Yet</h3>
            <p>Upload a PDF document to compare how XOrbit and VJA models extract data.</p>
            <button onClick={() => fileInputRef.current?.click()}>
              <Upload size={18} /> Upload PDF to Compare
            </button>
          </div>
        ) : (
          <table className="v1-comp-list-table">
            <thead>
              <tr>
                <th>ID</th>
                <th>Filename</th>
                <th>Match Rate</th>
                <th>XOrbit Time</th>
                <th>VJA Time</th>
                <th>Created</th>
                <th>Actions</th>
              </tr>
            </thead>
            <tbody>
              {filteredComparisons.map((comp) => {
                const matchPct = getMatchPercentage(comp);
                return (
                  <tr key={comp._id} onClick={() => navigate(`/v1/comparison/${comp._id}`)}>
                    <td className="v1-comp-id-cell">{comp._id?.slice(-8)}</td>
                    <td className="v1-comp-filename-cell" title={comp.filename}>
                      {comp.filename?.substring(0, 30) || "-"}
                      {comp.filename?.length > 30 && "..."}
                    </td>
                    <td>
                      <div className="v1-comp-match-badge" data-level={matchPct >= 80 ? "high" : matchPct >= 50 ? "medium" : "low"}>
                        {matchPct >= 80 ? <CheckCircle size={14} /> : matchPct >= 50 ? <AlertCircle size={14} /> : <XCircle size={14} />}
                        {matchPct}%
                      </div>
                    </td>
                    <td>{comp.xorbit_time ? `${comp.xorbit_time.toFixed(2)}s` : "-"}</td>
                    <td>{comp.vja_time ? `${comp.vja_time.toFixed(2)}s` : "-"}</td>
                    <td>{formatDate(comp.created_at)}</td>
                    <td className="v1-comp-actions-cell" onClick={(e) => e.stopPropagation()}>
                      <button
                        className="v1-comp-action-btn view"
                        title="View Comparison"
                        onClick={() => navigate(`/v1/comparison/${comp._id}`)}
                      >
                        <Eye size={16} />
                      </button>
                      <button
                        className="v1-comp-action-btn delete"
                        title="Delete"
                        onClick={(e) => handleDelete(comp._id, e)}
                      >
                        <Trash2 size={16} />
                      </button>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        )}
      </div>

      {/* Pagination */}
      {!loading && filteredComparisons.length > 0 && (
        <div className="v1-comp-pagination">
          <div className="v1-comp-pagination-info">
            Showing {comparisons.length > 0 ? pagination.skip + 1 : 0} - {Math.min(pagination.skip + pagination.limit, pagination.total)} of {pagination.total}
          </div>
          <div className="v1-comp-pagination-controls">
            <button onClick={handlePrevPage} disabled={pagination.page <= 1}>
              <ChevronLeft size={16} /> Previous
            </button>
            <span className="v1-comp-page-indicator">
              Page {pagination.page} of {pagination.pages || 1}
            </span>
            <button onClick={handleNextPage} disabled={pagination.page >= pagination.pages}>
              Next <ChevronRight size={16} />
            </button>
          </div>
        </div>
      )}
    </div>
  );
};

export default V1Comparison;
